// Bars. A meter is never colour alone: the track, the label and the value text
// are always there, so it still reads in greyscale and in a screenshot printed
// in black and white (UI-DESIGN §6).

import { div, h } from '../dom';

export type BarTone = 'clay' | 'good' | 'warn' | 'danger';

export interface BarOptions {
  /** 0–100, clamped so a broken selector can never draw a bar off the end. */
  value: number;
  label?: string;
  /** Right-hand text: a number in Veteran, a word in Normal (REFOCUS §6). */
  valueLabel?: string;
  tone?: BarTone;
  size?: 'sm' | 'md';
  /** Dashed remainder: the "still learning" look on the bird screen (U5). */
  pending?: boolean;
}

const BAR_TONE: Record<BarTone, string> = {
  clay: 'var(--cf-clay)',
  good: 'var(--cf-sage)',
  warn: 'var(--cf-warn)',
  danger: 'var(--cf-danger)',
};

export function bar(options: BarOptions): HTMLElement {
  const pct = Math.max(0, Math.min(100, Math.round(options.value)));
  const small = options.size === 'sm';
  const fill = h('div', {
    class: 'cf-bar__fill',
    style: { width: `${pct}%`, background: BAR_TONE[options.tone ?? 'clay'] },
  });
  // The learned part is solid and the rest is a dashed stub: how much of this
  // stat the player has actually earned, not a made-up percentage (REFOCUS §8).
  const track = h(
    'div',
    { class: small ? 'cf-bar__track cf-bar__track--sm' : 'cf-bar__track' },
    fill,
  );
  if (options.pending) {
    track.appendChild(
      h('div', {
        class: 'cf-bar__pending',
        style: { width: `${100 - pct}%` },
        attrs: { 'aria-hidden': 'true' },
      }),
    );
  }
  if (small && options.label === undefined) return track;

  return h(
    'div',
    { class: 'cf-bar' },
    h(
      'div',
      { class: 'cf-bar__head' },
      h('span', { class: small ? 'cf-tiny' : 'cf-small', text: options.label ?? '' }),
      h('span', {
        class: small ? 'cf-tiny cf-num' : 'cf-small cf-num',
        text: options.valueLabel ?? `${pct}%`,
      }),
    ),
    track,
  );
}

/** The budget row on the buy screen: label, spent/total, bar, and the count. */
export function budgetBar(options: {
  label: string;
  spent: number;
  total: number;
  unit: string;
  side: string;
}): HTMLElement {
  const pct = options.total > 0 ? Math.round((options.spent / options.total) * 100) : 0;
  return div(
    { class: 'cf-budget' },
    div(
      { class: 'cf-stack cf-stack--tight' },
      h(
        'div',
        { class: 'cf-budget__row' },
        h('span', { class: 'cf-label', text: options.label }),
        h('span', {
          class: 'cf-label cf-num',
          text: `${options.spent} / ${options.total} ${options.unit}`,
        }),
      ),
      h(
        'div',
        { class: 'cf-bar__track' },
        h('div', {
          class: 'cf-bar__fill',
          style: { width: `${Math.max(0, Math.min(100, pct))}%`, background: 'var(--cf-clay)' },
        }),
      ),
    ),
    h('span', { class: 'cf-budget__side', text: options.side }),
  );
}
